// client/src/components/activity/EvaluationForm.jsx
import React, { useState } from 'react';
import { Button, Card } from '../ui/UIComponents';

const ScaleQuestion = ({ question, value, onChange, required, error }) => {
  return (
    <div className="mb-6">
      <label className="block text-charcoal-800 font-medium mb-3 font-body">
        {question}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="flex items-center justify-center space-x-2">
        <span className="text-sm text-charcoal-500 mr-2 font-body">1</span>
        {[1, 2, 3, 4, 5].map(num => (
          <button
            key={num}
            type="button"
            onClick={() => onChange(num)}
            className={`flex items-center justify-center w-14 h-14 border-2 rounded-lg transition-all font-body ${
              value === num
                ? 'border-purple-600 bg-purple-50 shadow-md'
                : 'border-charcoal-300 bg-white hover:bg-cream-100 hover:border-charcoal-400'
            }`}
          >
            <span className={`text-lg font-medium ${
              value === num ? 'text-purple-600' : 'text-charcoal-600'
            }`}>
              {num}
            </span>
          </button>
        ))}
        <span className="text-sm text-charcoal-500 ml-2 font-body">5</span>
      </div>
      
      {error && (
        <p className="text-red-500 text-sm mt-2 font-body">{error}</p>
      )}
    </div>
  );
};

const TextQuestion = ({ question, value, onChange, placeholder }) => {
  return (
    <div className="mb-6">
      <label className="block text-charcoal-800 font-medium mb-3 font-body">
        {question}
      </label>
      
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 border border-charcoal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-body"
        rows={4}
        placeholder={placeholder || "Vul hier je suggesties in (optioneel)"}
      />
    </div>
  );
};

const CheckboxQuestion = ({ question, checked, onChange }) => {
  return (
    <div className="mb-6">
      <label className="flex items-start space-x-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="w-5 h-5 text-purple-600 mt-1 rounded"
        />
        <span className="text-charcoal-700 leading-relaxed font-body">{question}</span>
      </label>
    </div>
  );
};

const EvaluationForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    theorie: {
      theory_sufficient: null,
      external_sources_reviewed: null,
      theory_suggestions: ''
    },
    simulatie: {
      conversations_realistic: null,
      cases_relevant: null,
      feels_prepared: null,
      feedback_useful: null,
      training_valuable: null,
      final_assessment_good: null,
      simulation_suggestions: ''
    },
    organisatie: {
      platform_worked_well: null,
      practical_matters_good: null,
      organization_suggestions: ''
    },
    nieuwsbrief: {
      wants_updates: false
    }
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleScaleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    if (errors[`${section}.${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${section}.${field}`];
        return newErrors;
      });
    }
  };

  const handleTextChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleCheckboxChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    const requiredFields = [
      { section: 'theorie', field: 'theory_sufficient', label: 'Theorie voldoende' },
      { section: 'theorie', field: 'external_sources_reviewed', label: 'Extra bronnen doorgenomen' },
      { section: 'simulatie', field: 'conversations_realistic', label: 'Gesprekken realistisch' },
      { section: 'simulatie', field: 'cases_relevant', label: 'Casuïstiek relevant' },
      { section: 'simulatie', field: 'feels_prepared', label: 'Voelt toegerust' },
      { section: 'simulatie', field: 'feedback_useful', label: 'Feedback zinvol' },
      { section: 'simulatie', field: 'training_valuable', label: 'Training waardevol' },
      { section: 'simulatie', field: 'final_assessment_good', label: 'Eindopdracht goed' },
      { section: 'organisatie', field: 'platform_worked_well', label: 'Platform werkte goed' },
      { section: 'organisatie', field: 'practical_matters_good', label: 'Praktische zaken goed geregeld' }
    ];
    
    requiredFields.forEach(({ section, field, label }) => {
      if (formData[section][field] === null) {
        newErrors[`${section}.${field}`] = `${label} is verplicht`;
      }
    });
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      setErrors({ submit: 'Er is een fout opgetreden. Probeer het opnieuw.' });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-cream-100 overflow-y-auto z-50">
      <div className="min-h-screen p-6 py-12">
        <div className="max-w-4xl mx-auto">
          <Card>
            <h1 className="font-title text-4xl font-light text-purple-600 mb-4">
              Evaluatie Training
            </h1>
            
            <div className="mb-8 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-charcoal-700 leading-relaxed font-body">
                Wij willen deze training graag verbeteren, help ons door het 
                evaluatieformulier in te vullen! Vervolgens ontvang je het certificaat.
              </p>
            </div>

            <div className="mb-8 p-4 bg-cream-200 rounded-lg">
              <p className="text-sm font-semibold text-charcoal-700 mb-3 font-body">Schaal betekenis:</p>
              <div className="grid grid-cols-5 gap-2 text-xs text-charcoal-600 font-body">
                <div className="text-center">
                  <div className="font-medium mb-1">1</div>
                  <div>Geheel mee oneens</div>
                </div>
                <div className="text-center">
                  <div className="font-medium mb-1">2</div>
                  <div>Deels mee oneens</div>
                </div>
                <div className="text-center">
                  <div className="font-medium mb-1">3</div>
                  <div>Neutraal</div>
                </div>
                <div className="text-center">
                  <div className="font-medium mb-1">4</div>
                  <div>Deels mee eens</div>
                </div>
                <div className="text-center">
                  <div className="font-medium mb-1">5</div>
                  <div>Geheel mee eens</div>
                </div>
              </div>
            </div>

            {Object.keys(errors).length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 font-medium mb-2 font-body">
                  Vul alle verplichte velden in om door te gaan:
                </p>
                <ul className="list-disc ml-5 text-sm text-red-600 font-body">
                  {Object.values(errors).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-10">
                <h2 className="font-header text-2xl font-medium text-charcoal-800 mb-6 pb-2 border-b-2 border-cream-200">
                  Theorie
                </h2>
                
                <ScaleQuestion
                  question="De aangeboden theorie was voldoende om te kunnen starten met de simulatietrainingen."
                  value={formData.theorie.theory_sufficient}
                  onChange={(value) => handleScaleChange('theorie', 'theory_sufficient', value)}
                  required
                  error={errors['theorie.theory_sufficient']}
                />
                
                <ScaleQuestion
                  question="Ik heb de extra bronnen bij de theorie (externe links) doorgenomen."
                  value={formData.theorie.external_sources_reviewed}
                  onChange={(value) => handleScaleChange('theorie', 'external_sources_reviewed', value)}
                  required
                  error={errors['theorie.external_sources_reviewed']}
                />
                
                <TextQuestion
                  question="Ik heb de volgende suggesties voor de theorie:"
                  value={formData.theorie.theory_suggestions}
                  onChange={(value) => handleTextChange('theorie', 'theory_suggestions', value)}
                />
              </div>

              <div className="mb-10">
                <h2 className="font-header text-2xl font-medium text-charcoal-800 mb-6 pb-2 border-b-2 border-cream-200">
                  Simulatiegesprekken
                </h2>
                
                <ScaleQuestion
                  question="De gesprekken met de simulatiepatiënten verliepen realistisch."
                  value={formData.simulatie.conversations_realistic}
                  onChange={(value) => handleScaleChange('simulatie', 'conversations_realistic', value)}
                  required
                  error={errors['simulatie.conversations_realistic']}
                />
                
                <ScaleQuestion
                  question="De casuïstiek van de simulatiepatiënten sluit aan bij mijn dagelijkse praktijk."
                  value={formData.simulatie.cases_relevant}
                  onChange={(value) => handleScaleChange('simulatie', 'cases_relevant', value)}
                  required
                  error={errors['simulatie.cases_relevant']}
                />
                
                <ScaleQuestion
                  question="Ik voel me voldoende toegerust om in gesprekken met cliënten open vragen en reflecties toe te passen om verandertaal uit te lokken."
                  value={formData.simulatie.feels_prepared}
                  onChange={(value) => handleScaleChange('simulatie', 'feels_prepared', value)}
                  required
                  error={errors['simulatie.feels_prepared']}
                />
                
                <ScaleQuestion
                  question="De feedback op de gesprekken was zinvol."
                  value={formData.simulatie.feedback_useful}
                  onChange={(value) => handleScaleChange('simulatie', 'feedback_useful', value)}
                  required
                  error={errors['simulatie.feedback_useful']}
                />
                
                <ScaleQuestion
                  question="Deze manier van trainen vind ik waardevol."
                  value={formData.simulatie.training_valuable}
                  onChange={(value) => handleScaleChange('simulatie', 'training_valuable', value)}
                  required
                  error={errors['simulatie.training_valuable']}
                />
                
                <ScaleQuestion
                  question="De eindopdracht was een goede manier om na te gaan of de leerdoelen behaald zijn."
                  value={formData.simulatie.final_assessment_good}
                  onChange={(value) => handleScaleChange('simulatie', 'final_assessment_good', value)}
                  required
                  error={errors['simulatie.final_assessment_good']}
                />
                
                <TextQuestion
                  question="Ik heb de volgende suggesties op het gebied van de simulatietrainingen:"
                  value={formData.simulatie.simulation_suggestions}
                  onChange={(value) => handleTextChange('simulatie', 'simulation_suggestions', value)}
                />
              </div>

              <div className="mb-10">
                <h2 className="font-header text-2xl font-medium text-charcoal-800 mb-6 pb-2 border-b-2 border-cream-200">
                  Organisatie
                </h2>
                
                <ScaleQuestion
                  question="De online leeromgeving werkte technisch goed."
                  value={formData.organisatie.platform_worked_well}
                  onChange={(value) => handleScaleChange('organisatie', 'platform_worked_well', value)}
                  required
                  error={errors['organisatie.platform_worked_well']}
                />
                
                <ScaleQuestion
                  question="De praktische zaken rondom de training, zoals betaling, toegang en contact met LerenMotiveren, waren goed geregeld."
                  value={formData.organisatie.practical_matters_good}
                  onChange={(value) => handleScaleChange('organisatie', 'practical_matters_good', value)}
                  required
                  error={errors['organisatie.practical_matters_good']}
                />
                
                <TextQuestion
                  question="Ik heb de volgende suggesties op het gebied van organisatie en techniek:"
                  value={formData.organisatie.organization_suggestions}
                  onChange={(value) => handleTextChange('organisatie', 'organization_suggestions', value)}
                />
              </div>

              <div className="mb-10">
                <h2 className="font-header text-2xl font-medium text-charcoal-800 mb-6 pb-2 border-b-2 border-cream-200">
                  Volgende trainingen
                </h2>
                
                <CheckboxQuestion
                  question="Ik wil op de hoogte worden gehouden wanneer er nieuwe trainingen beschikbaar zijn van LerenMotiveren"
                  checked={formData.nieuwsbrief.wants_updates}
                  onChange={(value) => handleCheckboxChange('nieuwsbrief', 'wants_updates', value)}
                />
              </div>

              <div className="flex justify-end pt-6 border-t-2 border-cream-200">
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  size="lg"
                >
                  {isSubmitting ? 'Verzenden...' : 'Verstuur Evaluatie'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EvaluationForm;